import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { difference } from 'lodash-es';

import { ReactComponent as FiltersIcon } from '../../../assets/images/filters.svg';
import { ReactComponent as AddIcon } from '../../../assets/images/plus-normal.svg';
import { ReactComponent as SyncIcon } from '../../../assets/images/sync.svg';
import GlobalFilter from '../../../components/GlobalFilter/GlobalFilter';
import Button from '../../../components/shared/Button/Button';
import Sortby from '../../../components/shared/Sortby/Sortby';
import { SORTBY_OPTIONS } from '../../../constants/commonConstants';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/paginationDefaults';
import { useWindowSize } from '../../../hooks/useWindowResize';
import {
  bulkDeleteContacts,
  createUserContacts,
  deleteUserContacts,
  getContacts,
  getTags,
  mergeContacts,
  setSearchTerm,
  setSortby,
  updateUserContacts,
} from '../../../redux/actions/networkActions';
import urls from '../../../routing/urls';

import AddContactModal from './AddContactModal/AddContactModal';
import BulkActionMenu from './BulkActionMenu/BulkActionMenu';
import ContactFilters from './ContactFilters/ContactFilters';
import { ContactsList } from './ContactsList/ContactsList';
import ContactsTable from './ContactsTable/ContactsTable';
import DeleteModal from './DeleteModal/DeleteModal';
import MergeModal from './MergeModal/MergeModal';

import styles from './Network.module.scss';

const Network = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const size = useWindowSize();
  const navigate = useNavigate();
  const isMobileScreen = useMemo(() => size.width <= 600, [size.width]);
  const contacts = useSelector((state) => state.networkReducer.contacts);
  const searchTerm = useSelector((state) => state.networkReducer.searchTerm);
  const pageInfo = useSelector((state) => state.networkReducer.pageInfo);
  const filters = useSelector((state) => state.filtersReducer.contactFilters);
  const sortby = useSelector((state) => state.networkReducer.sortby);
  const loading = useSelector((state) => state.networkReducer.networksLoading);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [filtersDrawer, setFiltersDrawer] = useState(false);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [deleteOption, setDeleteOption] = useState({ modal: false, contactDetails: {} });
  const [contactsToMerge, setContactsToMerge] = useState([]);

  const handleToggleDeleteModal = useCallback(() => {
    setShowDeleteModal((prevState) => !prevState);
  }, []);

  const handleToggleMergeModal = useCallback(() => {
    setShowMergeModal((prevState) => !prevState);
  }, []);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.referrals_sent) count += 1;
    if (filters.tags.length) count += 1;
    if (filters.sources.length) count += 1;
    if (filters.starred) count += 1;
    return count;
  }, [filters]);

  const handleOnToggleContact = useCallback(
    (rows) => {
      setSelectedContacts(rows);
      if (rows.length === 0) {
        setContactsToMerge([]);
      } else if (rows.length > contactsToMerge.length) {
        const newItems = difference(rows, contactsToMerge);
        setContactsToMerge([...contactsToMerge, ...newItems]);
      } else {
        const itemsToRemove = difference(contactsToMerge, rows);
        const filteredArray = contactsToMerge.filter((item) => itemsToRemove.find((newItem) => newItem !== item));
        setContactsToMerge(filteredArray);
      }
    },
    [selectedContacts, contactsToMerge]
  );

  const fetchUserContacts = (pageArg, perPageArg, searchArg, filtersArg, sortbyArg) => {
    setPage(pageArg);
    const formattedFilters = {
      ...filtersArg,
      tags: filtersArg.tags?.map(({ id }) => id),
      sources: filtersArg.sources?.map(({ value }) => value),
    };
    dispatch(getContacts(pageArg, perPageArg, searchArg || '', formattedFilters, sortbyArg?.value));
  };

  const mergeSuccessCallback = useCallback(() => {
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
    handleToggleMergeModal();
    setContactsToMerge([]);
  }, [page, perPage, searchTerm, filters, sortby]);

  const tagSuccessCallback = useCallback(() => {
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
  }, [page, perPage, searchTerm, filters, sortby]);

  const deleteSuccessCallback = useCallback(() => {
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
    handleToggleDeleteModal();
  }, [page, perPage, searchTerm, filters, sortby]);

  const handleDeleteYes = useCallback(() => {
    dispatch(bulkDeleteContacts(selectedContacts, deleteSuccessCallback));
  }, [selectedContacts, deleteSuccessCallback]);

  useEffect(() => {
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
  }, [page, perPage]);

  useEffect(() => {
    dispatch(getTags(true));
  }, []);

  useEffect(() => {
    if (!showAddUpdateModal) {
      setFormValues({});
    }
  }, [showAddUpdateModal]);

  const toggleFilters = useCallback(() => {
    setFiltersDrawer((prevState) => !prevState);
  }, []);

  const handleSearchChange = useCallback(
    (val) => {
      fetchUserContacts(DEFAULT_PAGE, perPage, val, filters, sortby);
      dispatch(setSearchTerm(val));
    },
    [perPage, filters, sortby]
  );

  const handleSortbyChange = useCallback(
    (val) => {
      fetchUserContacts(DEFAULT_PAGE, perPage, searchTerm, filters, val);
      dispatch(setSortby(val));
    },
    [perPage, searchTerm, sortby]
  );

  const handleOpenModal = (data = {}) => {
    const newData = { ...data };
    if (Object.keys(data).length) {
      if (newData.user_contact_phone_numbers_attributes.length === 0) {
        newData.user_contact_phone_numbers_attributes = [
          { number_type: '', phone_numbers_attributes: { full_number: '' } },
        ];
      }
      if (newData.user_contact_emails_attributes?.length === 0) {
        newData.user_contact_emails_attributes = [{ email: '', email_type: '' }];
      }
      if (newData.user_contact_websites_attributes?.length === 0) {
        newData.user_contact_websites_attributes = [{ website: '' }];
      }
      if (newData.custom_fields?.length === 0) {
        newData.user_contact_tags_attrbutes = [{ custom_field: '', label: '' }];
      }
    }
    setFormValues(newData);
    setShowAddUpdateModal(true);
  };

  const handleAddDeleteContact = useCallback(() => {
    setShowAddUpdateModal(false);
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
  }, [page, perPage, searchTerm, filters, sortby]);

  const handleContactUpdate = useCallback((data) => {
    dispatch(updateUserContacts(data, () => setShowAddUpdateModal(false)));
  }, []);

  const handleContactDelete = useCallback(
    (id, callback) => {
      dispatch(deleteUserContacts(id, callback ?? handleAddDeleteContact));
    },
    [handleAddDeleteContact]
  );

  const handleContactCreate = useCallback(
    (data) => {
      dispatch(createUserContacts(data, handleAddDeleteContact));
    },
    [handleAddDeleteContact]
  );

  const navigateToSyncSources = useCallback(() => {
    navigate(urls.syncSources);
  }, []);

  const toggleDeleteOptionModal = useCallback((contact) => {
    setDeleteOption((prevState) =>
      prevState.modal ? { modal: false, contactDetails: {} } : { modal: true, contactDetails: contact }
    );
  }, []);

  const singleContactDeleteCallback = useCallback(() => {
    toggleDeleteOptionModal();
    fetchUserContacts(page, perPage, searchTerm, filters, sortby);
  }, [page, perPage, searchTerm, filters, sortby]);

  const handleDeleteSingleContact = useCallback((id) => {
    handleContactDelete(id, singleContactDeleteCallback);
  }, []);

  const handleMergeYes = useCallback(() => {
    dispatch(mergeContacts(contactsToMerge, mergeSuccessCallback));
  }, [contactsToMerge]);

  return (
    <div className={styles.rootCont}>
      {showAddUpdateModal && (
        <AddContactModal
          setShowModal={setShowAddUpdateModal}
          showModal={showAddUpdateModal}
          formData={formValues}
          handleContactUpdate={handleContactUpdate}
          handleContactCreate={handleContactCreate}
          toggleDeleteOptionModal={toggleDeleteOptionModal}
        />
      )}
      <DeleteModal
        showModal={showDeleteModal}
        handleDeleteYes={handleDeleteYes}
        handleToggleDeleteModal={handleToggleDeleteModal}
        content={
          selectedContacts.length > 1
            ? t('network.membersCount', { count: selectedContacts.length })
            : t('network.oneMember')
        }
      />
      <MergeModal
        showModal={showMergeModal}
        handleToggleMergeModal={handleToggleMergeModal}
        contactsToMerge={contactsToMerge}
        handleMergeYes={handleMergeYes}
      />
      <DeleteModal
        showModal={deleteOption.modal}
        handleDeleteYes={() => handleDeleteSingleContact(deleteOption.contactDetails?.id)}
        handleToggleDeleteModal={toggleDeleteOptionModal}
        content={deleteOption.contactDetails?.full_name}
      />
      <ContactFilters
        toggleFilters={toggleFilters}
        filtersDrawer={filtersDrawer}
        getFilteredContacts={(updatedFilters) =>
          fetchUserContacts(DEFAULT_PAGE, perPage, searchTerm, updatedFilters, sortby)
        }
      />
      <div className={styles.searchfilterCont}>
        <GlobalFilter
          placeholder={t('placeholders.yourContact')}
          searchTerm={searchTerm}
          setSearchTerm={handleSearchChange}
        />
        <div onClick={toggleFilters} role="presentation" className={styles.filtersCont}>
          {!!appliedFiltersCount && <div className={styles.filtersCount}>{appliedFiltersCount}</div>}
          <FiltersIcon />
        </div>
      </div>
      <div className={styles.btnsCont}>
        <div>
          {selectedContacts.length > 0 ? (
            <BulkActionMenu
              selectedContacts={selectedContacts}
              handleToggleDeleteModal={handleToggleDeleteModal}
              handleToggleMergeModal={handleToggleMergeModal}
              contacts={contacts}
              tagSuccessCallback={tagSuccessCallback}
            />
          ) : (
            <div className={styles.rowCont}>
              <Button className={styles.syncBtn} onClick={navigateToSyncSources}>
                <SyncIcon />
                {t('network.syncSources')}
              </Button>
              <Button variant="outline-primary" className={styles.addBtn} onClick={() => handleOpenModal()}>
                <AddIcon />
                {t('network.addManually')}
              </Button>
            </div>
          )}
        </div>
        <div className={styles.sortbyCont}>
          <Sortby
            sortOptions={SORTBY_OPTIONS}
            defaultValue={SORTBY_OPTIONS[3]}
            value={sortby}
            onChange={handleSortbyChange}
          />
        </div>
      </div>
      {isMobileScreen ? (
        <ContactsList
          contacts={contacts}
          setPage={setPage}
          setPerPage={setPerPage}
          pageInfo={pageInfo}
          handleOpenModal={handleOpenModal}
          toggleDeleteOption={toggleDeleteOptionModal}
        />
      ) : (
        <ContactsTable
          selectedContacts={selectedContacts}
          handleOnToggleContact={handleOnToggleContact}
          contacts={contacts}
          setPage={setPage}
          perPage={perPage}
          setPerPage={setPerPage}
          pageInfo={pageInfo}
          handleOpenModal={handleOpenModal}
          loading={loading}
          navigateToSyncSources={navigateToSyncSources}
          toggleDeleteOption={toggleDeleteOptionModal}
          appliedFiltersCount={appliedFiltersCount}
          searchTerm={searchTerm}
        />
      )}
    </div>
  );
};

export default Network;
